// =====================================================================
// TrajetFormModal.jsx - Modal create/edit trajet (M-002, refactor Day 3)
// =====================================================================
// Day 3 changes vs Faza 2 Partea B (18 mai):
//   - SearchableSelect pentru project + carrier + driver (Option C pattern)
//   - Invoice fields support (invoiced + invoicedAt + invoicedByUserId)
//   - Banner avertisment + block edit/delete daca trip.invoiced=true
//   - Display invoice info in metadata section (edit mode)
//
// Props:
//   - tripId: null = create, string = edit existing
//   - onClose: () => void
//   - onSaved: () => void
//
// Permission logic (neschimbat):
//   - trips.create.all          -> POST nou
//   - trips.update.all          -> edit (orice status, oricand)
//   - trips.delete.all          -> buton Supprimer
//   - trips.complete.assigned   -> buton "Marquer comme terminé"
// =====================================================================

import { useState, useEffect } from 'react';
import {
  trips as tripsApi,
  users as usersApi,
  projects as projectsApi,
  tripCarriers as carriersApi,
  tripTaskTemplates as templatesApi,
  vehicles as vehiclesApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import SearchableSelect from '../../components/SearchableSelect.jsx';
import { marqueurInactif, trierPersonnes } from '../../lib/tri-personnes.js';
import { aujourdhuiBruxelles } from '../../lib/dates.js';
import { tachesValables, tacheValableLe } from '../../lib/validiteTaches.js';

const STATUS_LABELS_FR = {
  PLANNED:   'Planifié',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
};

const STATUS_BADGE_STYLE = {
  PLANNED:   { background: '#fef3c7', color: '#92400e' },
  COMPLETED: { background: '#d1fae5', color: '#065f46' },
  CANCELLED: { background: '#fee2e2', color: '#991b1b' },
};

const today = () => aujourdhuiBruxelles();   // date du jour à Bruxelles (lib/dates.js)

export default function TrajetFormModal({ tripId, onClose, onSaved }) {
  const { can } = useAuth();
  const isEdit = !!tripId;

  const canCreate   = can('trips.create.all');
  const canUpdate   = can('trips.update.all');
  const canDelete   = can('trips.delete.all');
  const canComplete = can('trips.complete.assigned');

  // Form state - section 1: informations générales
  const [date, setDate]                 = useState(today());
  // Catalogue du 01/11/2026 : tâches de trajet valables à la date du trajet (la tâche déjà enregistrée d'un
  // trajet existant reste affichée).
  const tachesDeLaLigne = (line) => tachesValables(line._tasksForProject, date, isEdit ? line.taskTemplateId : null);
  const [selectedCarrier, setCarrier]   = useState(null);
  const [selectedDriver, setDriver]     = useState(null);
  const [selectedVehicle, setVehicle]   = useState(null);
  const [ref, setRef]                   = useState('');
  const [status, setStatus]             = useState('PLANNED');

  // Section 2: itinéraire (Day 11 — kmTotal supprime ici, saisi par le chauffeur a la cloture)
  const [origin, setOrigin]             = useState('');
  const [destination, setDestination]   = useState('');
  const [kmTotal, setKmTotal]           = useState(0);
  const [waitingMinutes, setWaitMin]    = useState('');

  const [fuelSurcharge, setFuel]        = useState(false);
  const [commentaire, setCommentaire]   = useState('');

  // Day 11 Phase 1B — Lignes projet (multi-projets)
  // _line_id : id DB existant (edit mode). _project, _tasksForProject : helpers UI.
  // _cle : identifiant STABLE de la ligne à l'écran (l'index change si on en retire
  // une) — une réponse réseau tardive retrouve ainsi la bonne ligne.
  const nouvelleCle = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
  const emptyLine = () => ({ _cle: nouvelleCle(), _line_id: null, _project: null, _tasksForProject: [],
                              projectId: '', taskTemplateId: '', prixAchat: '', prixVente: '', info: '', noteChauffeur: '',
                              origin: '', destination: '', waitingMinutes: '' });
  const [lines, setLines]               = useState(() => [emptyLine()]);
  // Nouveau trajet : la date change → une tâche choisie qui n'est plus valable à cette date passe sur sa
  // remplaçante (« 9. Support client » → « 13. Support client »), ou se vide s'il n'y en a pas.
  useEffect(() => {
    if (isEdit) return;
    setLines((prev) => prev.map((l) => {
      const t = l.taskTemplateId && l._tasksForProject.find((x) => x.id === l.taskTemplateId);
      if (!t || tacheValableLe(t, date)) return l;
      const suivante = l._tasksForProject.find((x) => x.label === t.replacedByName && tacheValableLe(x, date))
        || l._tasksForProject.find((x) => x.replacedByName === t.label && tacheValableLe(x, date));
      return { ...l, taskTemplateId: suivante ? suivante.id : '' };
    }));
  }, [date, isEdit]);

  // Metadata edit-only
  const [createdByName, setCreator]     = useState('');
  const [createdAt, setCreatedAt]       = useState(null);
  const [updatedAt, setUpdatedAt]       = useState(null);
  const [completedByName, setCompBy]    = useState('');
  const [completedAt, setCompletedAt]   = useState(null);
  const [driverComment, setDrvComment]  = useState('');
  const [durationMinutes, setDuration]  = useState(null);

  // Invoice (Day 3 NEW)
  const [invoiced, setInvoiced]         = useState(false);
  const [invoicedAt, setInvoicedAt]     = useState(null);
  const [invoicedByName, setInvBy]      = useState('');

  // Dropdown data
  const [allProjects, setAllProjects]   = useState([]);
  const [allCarriers, setAllCarriers]   = useState([]);
  const [allDrivers, setAllDrivers]     = useState([]);
  const [allVehicles, setAllVehicles]   = useState([]);    // Day 11
  const [taskTemplates, setTaskTpls]    = useState([]);

  // UI state
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [readOnly, setReadOnly] = useState(false);

  // Load dropdowns + (if edit) trip data
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [projList, carrList, drvList, vehList] = await Promise.all([
          projectsApi.list({ isActive: true }),   // la clé du serveur est isActive ; pageSize n'existe pas
          carriersApi.list({ isActive: true }),
          usersApi.list({ driver: 'true', active: 'true' }),
          vehiclesApi.list(),                                        // Day 11
        ]);
        if (cancelled) return;
        const projects = Array.isArray(projList) ? projList : (projList.items || []);
        const carriers = Array.isArray(carrList) ? carrList : (carrList.items || []);
        const drivers  = Array.isArray(drvList)  ? drvList  : (drvList.items  || []);
        const vehs     = Array.isArray(vehList)  ? vehList  : (vehList.items  || []);
        setAllProjects(projects.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setAllCarriers(carriers.sort((a, b) => (a.label || '').localeCompare(b.label || '')));
        setAllDrivers(drivers);
        setAllVehicles(vehs.sort((a, b) => (a.licensePlate || '').localeCompare(b.licensePlate || '')));

        if (isEdit) {
          const trip = await tripsApi.get(tripId);
          if (cancelled) return;
          setRef(trip.ref || '');
          setStatus(trip.status || 'PLANNED');
          setDate(trip.date ? trip.date.slice(0, 10) : today());
          // Resolve project/carrier/driver objects from loaded lists
          // Même cas que le chauffeur : un transporteur désactivé depuis n'est plus
          // dans la liste — le champ obligatoire se vidait et rien ne s'enregistrait.
          const transporteur = carriers.find(c => c.id === trip.carrierId)
            || (trip.carrier ? { ...trip.carrier, label: `${trip.carrier.label} (désactivé)` } : null);
          if (transporteur && !carriers.some(c => c.id === transporteur.id)) setAllCarriers([...carriers, transporteur]);
          setCarrier(transporteur);
          // Le chauffeur du trajet peut avoir été désactivé depuis : il n'est
          // alors pas dans la liste des éligibles, le champ se vidait tout seul
          // et plus rien ne pouvait être enregistré — pas même le kilométrage.
          // Le serveur, lui, accepte qu'on garde le même : il ne revalide que
          // si le chauffeur CHANGE. On le remet donc dans la liste, marqué.
          // Absent de la liste : compte désactivé, droit retiré (ou refusé par une
          // dérogation), ou droit implicite d'un accès total. On le garde avec son
          // vrai statut ; seul « (inactif) » est affirmé, parce qu'il est sûr.
          const chauffeurDuTrajet = drivers.find(d => d.id === trip.driverId)
            || trip.driver || null;
          if (chauffeurDuTrajet && !drivers.some(d => d.id === chauffeurDuTrajet.id)) {
            setAllDrivers(trierPersonnes([...drivers, chauffeurDuTrajet]));
          }
          setDriver(chauffeurDuTrajet);
          // Véhicule désactivé depuis : il quittait la liste, le champ se vidait et
          // l'enregistrement effaçait le véhicule du trajet sans rien dire.
          const vehicule = vehs.find(v => v.id === trip.vehicleId)
            || (trip.vehicle ? { ...trip.vehicle, label: `${trip.vehicle.label || trip.vehicle.licensePlate} (désactivé)` } : null);
          if (vehicule && !vehs.some(v => v.id === vehicule.id)) setAllVehicles([...vehs, vehicule]);
          setVehicle(vehicule);
          setOrigin(trip.origin || '');
          setDestination(trip.destination || '');
          setKmTotal(trip.kmTotal ?? 0);
          setWaitMin(trip.waitingMinutes ?? '');
          setFuel(!!trip.fuelSurcharge);
          setCommentaire(trip.commentaire || '');

          // Day 11 — Restore lines (with their tasks per project)
          if (Array.isArray(trip.lines) && trip.lines.length > 0) {
            const lineRows = await Promise.all(trip.lines.map(async (l) => {
              // Le projet de la ligne peut avoir été archivé depuis le trajet : il
              // n'est plus dans la liste des actifs. Le serveur le renvoie avec la
              // ligne ; on le garde, nommé, plutôt qu'un champ vide.
              const proj = projects.find(p => p.id === l.projectId)
                || (l.project ? { ...l.project, name: `${l.project.name} (archivé)` } : null);
              let tasks = [];
              let tachesIndisponibles = false;   // liste non chargée ≠ tâche désactivée
              if (l.projectId) {
                try {
                  const tpls = await templatesApi.list({ projectId: l.projectId, isActive: true });
                  tasks = Array.isArray(tpls) ? tpls : (tpls.items || []);
                } catch (_) { tachesIndisponibles = true; }
              }
              return {
                _cle:             nouvelleCle(),
                _line_id:         l.id,
                _project:         proj,
                _tasksForProject: tasks,
                projectId:        l.projectId,
                taskTemplateId:   l.taskTemplateId || '',
                _tacheActuelle:   l.taskTemplate || null,   // affichée même si désactivée depuis
                _tachesIndisponibles: tachesIndisponibles,
                prixAchat:        l.prixAchat ?? '',
                prixVente:        l.prixVente ?? '',
                info:             l.info || '',
                noteChauffeur:    l.noteChauffeur || '',
                origin:           l.origin || '',
                destination:      l.destination || '',
                waitingMinutes:   l.waitingMinutes != null ? String(l.waitingMinutes) : '',
              };
            }));
            if (!cancelled) setLines(lineRows);
          }
          setCreator(trip.createdBy?.name || '');
          setCreatedAt(trip.createdAt || null);
          setUpdatedAt(trip.updatedAt || null);
          setCompBy(trip.completedBy?.name || '');
          setCompletedAt(trip.completedAt || null);
          setDrvComment(trip.driverComment || '');
          setDuration(trip.durationMinutes ?? null);

          // Invoice (Day 3 NEW)
          setInvoiced(!!trip.invoiced);
          setInvoicedAt(trip.invoicedAt || null);
          setInvBy(trip.invoicedBy?.name || '');

          // Read-only daca trip e facturat sau user n-are perm de update
          const canEdit = canUpdate && !trip.invoiced;
          setReadOnly(!canEdit);

        } else {
          // Create mode: preview next ref + carrier default = BRAIN (interna)
          try {
            const { ref: nextRef } = await tripsApi.nextRef();
            if (!cancelled) setRef(nextRef);
          } catch (_e) { /* nu blochez form */ }

          const internalDefault = carriers.find(c => c.code && c.code.toUpperCase().includes('BRAIN'));
          if (!cancelled && internalDefault) setCarrier(internalDefault);

          if (!canCreate) setReadOnly(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[TrajetFormModal] load error', err);
          setError(err.response?.data?.message || 'Erreur lors du chargement');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [tripId, isEdit, canCreate, canUpdate]);

  // Day 11 — Lines helpers
  const updateLine = (idx, patch) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  };

  const setLineProject = async (idx, project) => {
    const cle = lines[idx]?._cle;
    setLines(prev => prev.map((l, i) => i === idx
      ? { ...l, _project: project, projectId: project?.id || '', taskTemplateId: '', _tasksForProject: [], _tachesIndisponibles: false }
      : l));
    if (project?.id) {
      try {
        const tpls = await templatesApi.list({ projectId: project.id, isActive: true });
        const tasks = Array.isArray(tpls) ? tpls : (tpls.items || []);
        // Retrouvée par sa clé (pas par l'index) ; ignorée si son projet a changé entre-temps.
        setLines(prev => prev.map((l) => (l._cle === cle && l.projectId === project.id) ? { ...l, _tasksForProject: tasks } : l));
      } catch (err) {
        console.error('[setLineProject] tasks load', err);
        // Dit à l'écran : sinon « aucune tâche configurée » faisait croire à un projet sans tâches.
        setLines(prev => prev.map((l) => (l._cle === cle && l.projectId === project.id) ? { ...l, _tachesIndisponibles: true } : l));
      }
    }
  };

  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

  // Escape to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    if (!selectedCarrier || !selectedDriver) {
      setError('Veuillez remplir tous les champs obligatoires (transporteur, chauffeur).');
      return;
    }
    if (!lines.length) {
      setError('Au moins une ligne projet est requise.');
      return;
    }
    for (const [i, l] of lines.entries()) {
      if (!l.projectId) {
        setError(`Ligne ${i + 1} : sélectionnez un projet.`);
        return;
      }
      if (!l.origin?.trim() || !l.destination?.trim()) {
        setError(`Ligne ${i + 1} : Origine et Destination sont obligatoires.`);
        return;
      }
    }

    setSaving(true);
    setError(null);

    try {
      const linesPayload = lines.map(l => ({
        projectId:      l.projectId,
        taskTemplateId: l.taskTemplateId || undefined,
        prixAchat:      l.prixAchat !== '' ? Number(l.prixAchat) : 0,
        prixVente:      l.prixVente !== '' ? Number(l.prixVente) : 0,
        info:           l.info?.trim() || undefined,
        noteChauffeur:  l.noteChauffeur?.trim() || undefined,
        origin:         l.origin.trim(),
        destination:    l.destination.trim(),
        waitingMinutes: l.waitingMinutes ? Number(l.waitingMinutes) : undefined,
      }));

      const headerPayload = {
        date,
        carrierId: selectedCarrier.id,
        driverId:  selectedDriver.id,
        vehicleId: selectedVehicle?.id || null,
        fuelSurcharge: !!fuelSurcharge,
      };

      if (isEdit) {
        // Day 11 Task#44 — patch lignes (id + champs editables) + kmTotal header
        const lineUpdates = lines
          .filter((l) => l._line_id)        // seules lignes deja en DB
          .map((l) => ({
            id:        l._line_id,
            prixAchat: l.prixAchat !== '' ? Number(l.prixAchat) : 0,
            prixVente: l.prixVente !== '' ? Number(l.prixVente) : 0,
            info:      l.info?.trim() || null,
            noteChauffeur: l.noteChauffeur?.trim() || null,
            origin:    l.origin?.trim() || undefined,
            destination: l.destination?.trim() || undefined,
            waitingMinutes: l.waitingMinutes ? Number(l.waitingMinutes) : null,
          }));
        await tripsApi.update(tripId, {
          ...headerPayload,
          kmTotal: Number(kmTotal) || 0,
          ...(lineUpdates.length > 0 ? { lines: lineUpdates } : {}),
        });
      } else {
        await tripsApi.create({ ...headerPayload, lines: linesPayload });
      }
      onSaved();
    } catch (err) {
      console.error('[TrajetFormModal] save error', err);
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement");
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !isEdit || invoiced) return;
    if (!confirm(`Supprimer le trajet ${ref} ?\n\nCette action est irréversible.`)) return;
    setSaving(true);
    setError(null);
    try {
      await tripsApi.remove(tripId);
      onSaved();
    } catch (err) {
      console.error('[TrajetFormModal] delete error', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (!canComplete || !isEdit || status !== 'PLANNED') return;

    // Day 11 — chauffeur saisit : km total (toute la course) + duree par ligne projet.
    const kmStr = window.prompt(
      `Marquer le trajet ${ref} comme terminé.\n\nKm total parcourus pour TOUTE la course (obligatoire) :`,
      '0'
    );
    if (kmStr === null) return;
    const kmNum = parseInt(kmStr, 10);
    if (isNaN(kmNum) || kmNum < 0 || kmNum > 99999) {
      alert('Km invalides.');
      return;
    }

    const lineDurations = [];
    for (const [i, l] of lines.entries()) {
      const projectName = l._project?.name || l.projectId || `Ligne ${i + 1}`;
      const durStr = window.prompt(
        `Durée pour la ligne ${i + 1} — ${projectName}\n(minutes, 1-1440) :`,
        '60'
      );
      if (durStr === null) return;
      const durNum = parseInt(durStr, 10);
      if (isNaN(durNum) || durNum < 1 || durNum > 1440) {
        alert(`Durée invalide pour ligne ${i + 1}.`);
        return;
      }
      if (!l._line_id) {
        alert(`Erreur interne: ligne ${i + 1} sans id DB. Rechargez la page.`);
        return;
      }
      lineDurations.push({ id: l._line_id, durationMinutes: durNum });
    }

    const driverCommentPrompt = window.prompt(
      `Commentaire chauffeur (optionnel, max 500 caractères) :`,
      ''
    );
    if (driverCommentPrompt === null) return;

    setSaving(true);
    setError(null);
    try {
      await tripsApi.complete(tripId, {
        kmTotal: kmNum,
        lines: lineDurations,
        driverComment: driverCommentPrompt.trim() || undefined,
      });
      onSaved();
    } catch (err) {
      console.error('[TrajetFormModal] complete error', err);
      setError(err.response?.data?.message || 'Erreur lors de la complétion');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={backdropStyle} onClick={onClose}>
        <div onClick={e => e.stopPropagation()} style={{ ...formStyle, padding: 40, textAlign: 'center' }}>
          Chargement...
        </div>
      </div>
    );
  }

  const statusBadge = isEdit ? (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 3,
      fontSize: 11, fontWeight: 500, marginLeft: 6,
      ...(STATUS_BADGE_STYLE[status] || {}),
    }}>
      {STATUS_LABELS_FR[status] || status}
    </span>
  ) : null;

  return (
    <div style={backdropStyle} onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={handleSubmit} style={formStyle}>

        {/* Header */}
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
            {isEdit ? (readOnly ? 'Détails du trajet' : 'Modifier le trajet') : 'Nouveau trajet'}
            {statusBadge}
          </h2>
          <div style={refBadgeStyle}>{ref || '(auto)'}</div>
        </div>

        {/* Invoice warning banner (Day 3 NEW) */}
        {invoiced && (
          <div style={invoicedBannerStyle}>
            ⚠ Ce trajet est déjà <strong>facturé</strong>
            {invoicedAt && <> le {new Date(invoicedAt).toLocaleDateString('fr-BE')}</>}
            {invoicedByName && <> par {invoicedByName}</>}
            . Modifications et suppression bloquées.
          </div>
        )}

        {/* ReadOnly banner (per perm) */}
        {readOnly && !invoiced && (
          <div style={readOnlyBannerStyle}>
            Lecture seule — vous n'avez pas la permission de modifier ce trajet.
          </div>
        )}

        {/* Body */}
        <div style={bodyStyle}>

          {/* Section 1: Informations générales */}
          <div style={sectionLabel}>Informations générales</div>

          <label style={labelStyle}>
            <span style={labelTextStyle}>Date *</span>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
                   disabled={readOnly || saving} required style={inputStyle} />
          </label>

          <div style={labelStyle}>
            <span style={labelTextStyle}>Transporteur *</span>
            <SearchableSelect
              items={allCarriers}
              selected={selectedCarrier}
              onSelect={setCarrier}
              disabled={readOnly || saving}
              placeholder="🔍 Rechercher transporteur..."
              getPrimary={(c) => c.label}
              getSecondary={(c) => c.code}
              searchFields={[(c) => c.label, (c) => c.code]}
            />
          </div>

          <div style={labelStyle}>
            <span style={labelTextStyle}>
              Chauffeur * <span style={hintStyle}>(éligibles)</span>
            </span>
            <SearchableSelect
              items={allDrivers}
              selected={selectedDriver}
              onSelect={setDriver}
              disabled={readOnly || saving}
              placeholder="🔍 Rechercher chauffeur..."
              getPrimary={(u) => (u.name || u.username || u.id) + marqueurInactif(u)}
              getSecondary={(u) => u.username || ''}
              searchFields={[(u) => u.name, (u) => u.username]}
            />
          </div>

          {/* Day 11 — parc de vehicules */}
          <div style={{ ...labelStyle, gridColumn: '1 / -1' }}>
            <span style={labelTextStyle}>Véhicule <span style={hintStyle}>(optionnel)</span></span>
            <SearchableSelect
              items={allVehicles}
              selected={selectedVehicle}
              onSelect={setVehicle}
              disabled={readOnly || saving}
              placeholder="🔍 Plaque ou type de camionnette..."
              getPrimary={(v) => v.licensePlate}
              getSecondary={(v) => v.label}
              searchFields={[(v) => v.licensePlate, (v) => v.label]}
            />
          </div>

          {/* Day 11 — Lignes projet (multi-projets) */}
          <div style={{ ...sectionLabel, gridColumn: '1 / -1' }}>
            Lignes projet * <span style={hintStyle}>({lines.length} {lines.length > 1 ? 'lignes' : 'ligne'})</span>
          </div>

          {lines.map((line, idx) => (
            <div key={line._cle || idx} style={lineCardStyle}>
              <div style={lineCardHeaderStyle}>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>Ligne {idx + 1}</span>
                {lines.length > 1 && !readOnly && !isEdit && (
                  <button type="button" onClick={() => removeLine(idx)} disabled={saving}
                          style={lineRemoveBtnStyle} title="Supprimer cette ligne">✕</button>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  <span style={labelTextStyle}>Projet *</span>
                  <SearchableSelect
                    items={allProjects}
                    selected={line._project}
                    onSelect={(p) => setLineProject(idx, p)}
                    disabled={readOnly || saving || isEdit}
                    placeholder="🔍 Rechercher projet..."
                    getPrimary={(p) => p.name}
                    getSecondary={(p) => `${p.kind || ''} · ${p.id}`}
                    searchFields={[(p) => p.name, (p) => p.id]}
                  />
                </div>

                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  <span style={labelTextStyle}>
                    Tâche <span style={hintStyle}>
                      {line.projectId
                        ? (tachesDeLaLigne(line).length > 0
                            ? `(${tachesDeLaLigne(line).length} disponible${tachesDeLaLigne(line).length > 1 ? 's' : ''})`
                            : line._tachesIndisponibles
                              ? (isEdit ? '(liste des tâches indisponible)' : '(liste des tâches indisponible — choisissez à nouveau le projet)')
                              : line._tasksForProject.length > 0
                                ? '(aucune tâche valable à cette date)'
                                : '(aucune tâche configurée)')
                        : '(sélectionnez d\'abord un projet)'}
                    </span>
                  </span>
                  <select value={line.taskTemplateId}
                          onChange={(e) => updateLine(idx, { taskTemplateId: e.target.value })}
                          disabled={readOnly || saving || isEdit || !line.projectId || (line._tasksForProject.length === 0 && !line._tacheActuelle)}
                          style={inputStyle}>
                    <option value="">— Aucune —</option>
                    {tachesDeLaLigne(line).map(t => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                    {line._tacheActuelle && !line._tasksForProject.some(t => t.id === line._tacheActuelle.id) && (
                      <option value={line._tacheActuelle.id}>{line._tacheActuelle.label}{line._tachesIndisponibles ? '' : ' (désactivée)'}</option>
                    )}
                  </select>
                </label>

                {/* Day 8 — Itinéraire per-ligne (Origine / Destination / Temps d'attente) */}
                <label style={labelStyle}>
                  <span style={labelTextStyle}>Origine *</span>
                  <input type="text" value={line.origin}
                         onChange={(e) => updateLine(idx, { origin: e.target.value })}
                         disabled={readOnly || saving}
                         placeholder="BRAIN LOG" maxLength={200} style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  <span style={labelTextStyle}>Destination *</span>
                  <input type="text" value={line.destination}
                         onChange={(e) => updateLine(idx, { destination: e.target.value })}
                         disabled={readOnly || saving}
                         placeholder="BRUXELLES" maxLength={200} style={inputStyle} />
                </label>
                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  <span style={labelTextStyle}>Temps d'attente <span style={hintStyle}>(min, optionnel)</span></span>
                  <select value={line.waitingMinutes}
                          onChange={(e) => updateLine(idx, { waitingMinutes: e.target.value })}
                          disabled={readOnly || saving} style={inputStyle}>
                    <option value="">— Aucun —</option>
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                    <option value="45">45 min</option>
                    <option value="60">60 min (1h)</option>
                    <option value="75">75 min</option>
                    <option value="90">90 min (1h30)</option>
                  </select>
                </label>

                {(canCreate || canUpdate) && (
                  <>
                    <label style={labelStyle}>
                      <span style={labelTextStyle}>Prix achat <span style={hintStyle}>(€)</span></span>
                      <input type="number" min={0} step="0.01" value={line.prixAchat}
                             onChange={(e) => updateLine(idx, { prixAchat: e.target.value })}
                             disabled={readOnly || saving}
                             placeholder="0.00" style={inputStyle} />
                    </label>
                    <label style={labelStyle}>
                      <span style={labelTextStyle}>Prix vente <span style={hintStyle}>(€)</span></span>
                      <input type="number" min={0} step="0.01" value={line.prixVente}
                             onChange={(e) => updateLine(idx, { prixVente: e.target.value })}
                             disabled={readOnly || saving}
                             placeholder="0.00" style={inputStyle} />
                    </label>
                  </>
                )}

                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  <span style={labelTextStyle}>Info uniquement pour le client <span style={hintStyle}>(facturation, optionnel)</span></span>
                  <input type="text" value={line.info}
                         onChange={(e) => updateLine(idx, { info: e.target.value })}
                         disabled={readOnly || saving}
                         maxLength={500} style={inputStyle} />
                </label>

                <label style={{ ...labelStyle, gridColumn: '1 / -1' }}>
                  <span style={{ ...labelTextStyle, color: '#dc2626', fontWeight: 600 }}>Note uniquement pour le chauffeur <span style={{ ...hintStyle, color: '#f87171' }}>(interne, optionnel)</span></span>
                  <input type="text" value={line.noteChauffeur}
                         onChange={(e) => updateLine(idx, { noteChauffeur: e.target.value })}
                         disabled={readOnly || saving}
                         maxLength={500}
                         placeholder="Note visible uniquement par le chauffeur"
                         style={{ ...inputStyle, borderColor: '#fca5a5' }} />
                </label>
              </div>
            </div>
          ))}

          {!readOnly && !isEdit && (
            <button type="button" onClick={addLine} disabled={saving} style={addLineBtnStyle}>
              + Ajouter une ligne projet
            </button>
          )}

          {isEdit && (
            <div style={{ gridColumn: '1 / -1', padding: 8, background: '#f8fafc', borderRadius: 4, fontSize: 11, color: '#64748b' }}>
              ℹ️ Projet et tâche d'une ligne sont verrouillés. <b>Origine, destination, temps d'attente, prix, info et note chauffeur sont éditables.</b> Pour changer le projet/tâche, annulez et recréez.
            </div>
          )}

          {/* Day 8 — Section Itinéraire globale SUPPRIMÉE : Origine/Destination/Temps d'attente sont PAR LIGNE maintenant. */}

          {/* Day 11 Task#44 — km total ajustable en mode edit (saisi par chauffeur a la cloture) */}
          {isEdit && (
            <label style={labelStyle}>
              <span style={labelTextStyle}>Km total <span style={hintStyle}>(saisi par chauffeur, ajustable)</span></span>
              <input type="number" min={0} step="1" value={kmTotal}
                     onChange={(e) => setKmTotal(Number(e.target.value) || 0)}
                     disabled={readOnly || saving} style={inputStyle} />
            </label>
          )}

          {/* Day 11 — Prix par ligne projet maintenant; le supplement carburant reste trip-level. */}
          <label style={{ ...labelStyle, gridColumn: '1 / -1', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={fuelSurcharge}
                   onChange={e => setFuel(e.target.checked)}
                   disabled={readOnly || saving} />
            <span style={{ fontSize: 13 }}>Avec Supplément carburant</span>
          </label>

          {/* Note globale supprimée (Day 8) — remplacée par "Note uniquement pour le chauffeur" PAR LIGNE. */}

          {/* Info-box edit mode */}
          {isEdit && (
            <div style={infoBoxStyle}>
              <strong>Créé:</strong> {createdAt ? new Date(createdAt).toLocaleString('fr-BE') : '—'}
              {createdByName && <> par {createdByName}</>}
              &nbsp;|&nbsp;
              <strong>Modifié:</strong> {updatedAt ? new Date(updatedAt).toLocaleString('fr-BE') : '—'}
              {status === 'COMPLETED' && (
                <>
                  <br />
                  <strong>Terminé:</strong> {completedAt ? new Date(completedAt).toLocaleString('fr-BE') : '—'}
                  {completedByName && <> par {completedByName}</>}
                  {durationMinutes != null && <> · durée: {durationMinutes} min</>}
                  {driverComment && <> · <em>"{driverComment}"</em></>}
                </>
              )}
              {invoiced && (
                <>
                  <br />
                  <strong style={{ color: '#065f46' }}>Facturé:</strong>{' '}
                  {invoicedAt ? new Date(invoicedAt).toLocaleString('fr-BE') : '—'}
                  {invoicedByName && <> par {invoicedByName}</>}
                </>
              )}
            </div>
          )}

          {error && (
            <div style={errorBoxStyle}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <div>
            {isEdit && canDelete && !readOnly && !invoiced && (
              <button type="button" onClick={handleDelete} disabled={saving} style={btnDanger(saving)}>
                Supprimer
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {isEdit && canComplete && status === 'PLANNED' && !readOnly && !invoiced && (
              <button type="button" onClick={handleComplete} disabled={saving} style={btnComplete(saving)}>
                Marquer comme terminé
              </button>
            )}
            <button type="button" onClick={onClose} disabled={saving} style={btnSecondary(saving)}>
              {readOnly ? 'Fermer' : 'Annuler'}
            </button>
            {!readOnly && (
              <button type="submit" disabled={saving || invoiced} style={btnPrimary(saving)}>
                {saving ? '…' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

// =====================================================================
// Styles
// =====================================================================

const backdropStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px 20px', zIndex: 50, overflow: 'auto',
};

const formStyle = {
  background: 'white', borderRadius: 8, width: '100%', maxWidth: 620,
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
};

const headerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '14px 20px', borderBottom: '1px solid #e2e8f0',
};

const refBadgeStyle = {
  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12,
  color: '#0c447c', padding: '2px 8px',
  background: '#e6f1fb', borderRadius: 3,
};

const readOnlyBannerStyle = {
  padding: '8px 20px', background: '#fef3c7', color: '#78350f',
  fontSize: 12, borderBottom: '1px solid #fbbf24',
};

const invoicedBannerStyle = {
  padding: '10px 20px', background: '#fef3c7', color: '#92400e',
  fontSize: 13, borderBottom: '1px solid #fbbf24',
};

const bodyStyle = {
  padding: 20, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
};

const sectionLabel = {
  gridColumn: '1 / -1',
  fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.05em', color: '#64748b',
  marginTop: 4, marginBottom: -6,
};

const labelStyle = { display: 'flex', flexDirection: 'column' };

const labelTextStyle = {
  fontSize: 12, color: '#475569', marginBottom: 4, fontWeight: 500,
};

const hintStyle = {
  fontSize: 10, color: '#94a3b8', fontWeight: 400,
};

const inputStyle = {
  width: '100%', padding: 8, border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: 13, boxSizing: 'border-box',
};

const infoBoxStyle = {
  gridColumn: '1 / -1', padding: 10,
  background: '#f8fafc', border: '1px solid #e2e8f0',
  borderRadius: 4, fontSize: 11, color: '#64748b',
  lineHeight: 1.6,
};

const errorBoxStyle = {
  gridColumn: '1 / -1', padding: 10,
  background: '#fee2e2', color: '#991b1b',
  border: '1px solid #fca5a5', borderRadius: 4, fontSize: 13,
};

const footerStyle = {
  padding: '12px 20px', borderTop: '1px solid #e2e8f0',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
};

const btnPrimary = (saving) => ({
  padding: '8px 16px', background: '#2563eb', color: 'white',
  border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 500,
  cursor: saving ? 'not-allowed' : 'pointer',
  opacity: saving ? 0.6 : 1,
});

const btnSecondary = (saving) => ({
  padding: '8px 16px', background: 'white', color: '#1e293b',
  border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
  cursor: saving ? 'not-allowed' : 'pointer',
});

const btnDanger = (saving) => ({
  padding: '8px 14px', background: 'white', color: '#991b1b',
  border: '1px solid #fca5a5', borderRadius: 4, fontSize: 13,
  cursor: saving ? 'not-allowed' : 'pointer',
});

const btnComplete = (saving) => ({
  padding: '8px 14px', background: '#059669', color: 'white',
  border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 500,
  cursor: saving ? 'not-allowed' : 'pointer',
  opacity: saving ? 0.6 : 1,
});

// Day 11 — Lignes projet styles
const lineCardStyle = {
  gridColumn: '1 / -1', border: '1px solid #e2e8f0', borderRadius: 6,
  padding: 12, background: '#fafbfc',
};
const lineCardHeaderStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #e2e8f0',
};
const lineRemoveBtnStyle = {
  background: 'transparent', border: 0, color: '#dc2626', cursor: 'pointer',
  fontSize: 16, width: 24, height: 24, display: 'flex', alignItems: 'center',
  justifyContent: 'center', borderRadius: 4,
};
const addLineBtnStyle = {
  gridColumn: '1 / -1', padding: '10px 14px',
  background: '#eff6ff', color: '#1d4ed8',
  border: '1px dashed #93c5fd', borderRadius: 6,
  cursor: 'pointer', fontSize: 13, fontWeight: 500,
};
